import { __ } from '@wordpress/i18n';
import { Mail, Phone, Landmark, MapPin, Calendar } from 'lucide-react';
import {
    CustomerAsyncSelect,
    DokanTooltip as Tooltip,
} from '@dokan/components';
import { decodeEntities } from '@wordpress/html-entities';
import { truncate } from '@dokan/utilities';
import { dateI18n, getSettings } from '@wordpress/date';

const CustomerInformation = ( {
    selectedCustomer,
    setSelectedCustomer,
    setIsCustomerModalOpen,
    setIsShippingModalOpen,
    shippingAddress,
    shippingDetails,
    setShippingDetails,
    quoteTitle,
} ) => {
    const isGuest = selectedCustomer && selectedCustomer.id === 0;
    return (
        <div className="w-80">
            <div className="bg-white rounded-lg shadow">
                { /* Header */ }
                <div className="customer-information-section p-5 border-b border-gray-200 space-y-4">
                    <h2 className="text-lg font-semibold text-[#25252D]">
                        { __( 'Customer Information', 'dokan' ) }
                    </h2>

                    { /* Customer Selects Dropdown */ }
                    <CustomerAsyncSelect
                        value={
                            selectedCustomer
                                ? {
                                      value: selectedCustomer.id,
                                      label: isGuest
                                          ? quoteTitle ||
                                            selectedCustomer.name ||
                                            __( 'Guest', 'dokan' )
                                          : selectedCustomer.name ||
                                            selectedCustomer.email,
                                      raw: selectedCustomer,
                                  }
                                : null
                        }
                        onChange={ ( opt ) => {
                            if ( opt ) {
                                setSelectedCustomer( {
                                    id: opt.value,
                                    name:
                                        opt.raw.first_name && opt.raw.last_name
                                            ? `${ opt.raw.first_name } ${ opt.raw.last_name }`
                                            : opt.raw.email,
                                    email: opt.raw?.email || '',
                                    phone:
                                        opt.raw?.billing?.phone ||
                                        selectedCustomer?.phone ||
                                        '',
                                    company:
                                        opt.raw?.billing?.company ||
                                        selectedCustomer?.company ||
                                        '',
                                } );

                                if ( opt.raw?.shipping ) {
                                    setShippingDetails( {
                                        ...shippingDetails,
                                        country:
                                            opt.raw?.billing?.country || '',
                                        state_name:
                                            opt.raw?.billing?.state || '',
                                        address_line_1:
                                            opt.raw?.billing?.address_1 || '',
                                        address_line_2:
                                            opt.raw?.billing?.address_2 || '',
                                        post_code:
                                            opt.raw?.billing?.postcode || '',
                                    } );
                                }
                            } else {
                                setSelectedCustomer( null );
                                setShippingDetails( {
                                    country: '',
                                    state_name: '',
                                    address_line_1: '',
                                    address_line_2: '',
                                    post_code: '',
                                    expected_delivery: '',
                                } );
                            }
                        } }
                        endpoint="dokan/v1/request-for-quote/customers"
                        placeholder={ __( 'Select Customer', 'dokan' ) }
                        isClearable
                        defaultOptions
                    />
                </div>

                { /* Overview Section */ }
                <div className="p-5 border-b border-gray-200 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-[#25252D]">
                            { __( 'Overview', 'dokan' ) }
                        </h3>
                        <button
                            onClick={ () => setIsCustomerModalOpen( true ) }
                            className="text-[#7047EB] text-sm font-medium hover:underline"
                        >
                            { __( 'Edit', 'dokan' ) }
                        </button>
                    </div>
                    <div className="space-y-4">
                        <div className="flex gap-2.5">
                            <span className="min-w-4 mt-0.5">
                                <Mail size={ 16 } color={ '#7C7C7C' } />
                            </span>
                            <span className="text-sm text-[#828282]">
                                { selectedCustomer?.email ||
                                    __( 'Email', 'dokan' ) }
                            </span>
                        </div>
                        <div className="flex gap-2.5">
                            <span className="min-w-4 mt-0.5">
                                <Phone size={ 16 } color={ '#7C7C7C' } />
                            </span>
                            <span className="text-sm text-[#828282]">
                                { selectedCustomer?.phone ||
                                    __( 'Phone Number', 'dokan' ) }
                            </span>
                        </div>
                        <div className="flex gap-2.5">
                            <span className="min-w-4 mt-0.5">
                                <Landmark size={ 16 } color={ '#7C7C7C' } />
                            </span>
                            <span className="text-sm text-[#828282]">
                                { selectedCustomer?.company ||
                                    __( 'Company Name', 'dokan' ) }
                            </span>
                        </div>
                    </div>
                </div>

                { /* Shipping Details Section */ }
                <div className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-[#25252D]">
                            { __( 'Shipping Details', 'dokan' ) }
                        </h3>
                        <button
                            onClick={ () => setIsShippingModalOpen( true ) }
                            className="text-[#7047EB] text-sm font-medium hover:underline"
                        >
                            { __( 'Edit', 'dokan' ) }
                        </button>
                    </div>
                    <div className="space-y-4">
                        <div className="flex gap-2.5">
                            <span className="min-w-4 mt-0.5">
                                <MapPin size={ 16 } color={ '#7C7C7C' } />
                            </span>
                            <span className="text-sm text-[#828282]">
                                { shippingAddress?.length <= 50 ? (
                                    decodeEntities( shippingAddress )
                                ) : (
                                    <Tooltip
                                        content={ decodeEntities(
                                            shippingAddress
                                        ) }
                                    >
                                        <span>
                                            { decodeEntities(
                                                truncate
                                                    ? truncate(
                                                          shippingAddress,
                                                          50
                                                      )
                                                    : shippingAddress
                                            ) }
                                        </span>
                                    </Tooltip>
                                ) }
                            </span>
                        </div>
                        <div className="flex gap-2.5">
                            <span className="min-w-4 mt-0.5">
                                <Calendar size={ 16 } color={ '#7C7C7C' } />
                            </span>
                            <div className="text-sm text-[#828282]">
                                <div>
                                    { __( 'Expected Delivery on', 'dokan' ) }
                                </div>
                                { shippingDetails.expected_delivery && (
                                    <div>
                                        { dateI18n(
                                            getSettings().formats.date,
                                            shippingDetails.expected_delivery,
                                            getSettings().timezone.string
                                        ) }
                                    </div>
                                ) }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerInformation;
